import { uiSurface } from "shared/ui/uiSurface"
import { AddCircleOutline, Delete } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useEffect } from "react"
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useAppSelector } from "src/app/hooks"
import { getSnapshotValue } from "src/app/slices/dispatcherRoutePlan.slice"
import {
  InputFieldWrapper,
  MenuWrapper,
} from "src/features/shared-module/components"
import { appRegexConstants } from "src/features/shared-module/constants"
import {
  getRandomHexColor,
  groupByDetail,
} from "src/features/shared-module/functions/common.functions"
import { useCreateRouteMutation, useUpdateRouteMutation } from "../apis"
import { BoundTypeEnum, routePlanningFormConstants } from "../constants"
import { RoutePlanningModel } from "../models"

export const DispatcherRouteForm: FC<{
  onSubmit: any
  onCancel: any
  routeDetail?: RoutePlanningModel
}> = ({ onSubmit, onCancel, routeDetail = null }) => {
  const boundOptions = [
    BoundTypeEnum.postal_code,
    BoundTypeEnum.locality,
    BoundTypeEnum.user_drawn_area,
  ]

  const { t } = useTranslation()
  const [createRouteFn, { isLoading: isCreateLoading }] =
    useCreateRouteMutation()
  const [updateRouteFn, { isLoading: isUpdateLoading }] =
    useUpdateRouteMutation()

  const mapDetail = useAppSelector(
    (state) => state.routePlanning.drawingDetail.now,
  )

  const formProps = useForm<RoutePlanningModel>({
    defaultValues: {
      name: "",
      color: getRandomHexColor(),
      description: "",
      bounds: [],
    },
    mode: "onChange",
  })

  const { fields: boundList, append } = useFieldArray({
    control: formProps.control,
    name: "bounds",
    rules: {
      validate: (value) => value.length > 0,
    },
  })

  useEffect(() => {
    if (!routeDetail) return
    formProps.reset(routeDetail)
  }, [routeDetail, formProps])

  const onChangeBoundType = (boundType: BoundTypeEnum) => {
    if (!boundList.some((e) => e.boundType === boundType)) {
      append({
        id: Math.random(),
        name: "",
        boundType: boundType,
        values: [defaultEmptyCode],
      })
    }
  }

  const handleSubmit = async (route: RoutePlanningModel) => {
    if (isCreateLoading || isUpdateLoading) return
    const index = route.bounds.findIndex(
      (e) => e.boundType === BoundTypeEnum.user_drawn_area,
    )
    if (index > -1) {
      console.log(mapDetail, "mapDetail")
      const list = mapDetail.map((e) => {
        return {
          value: getSnapshotValue(e.geometry),
          boundType: e.type,
        }
      })
      const data = groupByDetail(list, "boundType")
      console.log(list, data)
    }
    try {
      if (routeDetail) {
        await updateRouteFn(route).unwrap()
      } else {
        await createRouteFn(route).unwrap()
      }
      onSubmit()
    } catch (error) {
      console.error("Failed to add route:", error)
    }
  }

  return (
    <FormProvider {...formProps}>
      <Typography variant="subtitle1" fontWeight={600}>
        Dispatcher route plan
      </Typography>
      <form {...uiSurface("UI-DSP-043")} data-testid="route-form" onSubmit={formProps.handleSubmit(handleSubmit)}>
        <InputFieldWrapper
          data-testid="route-form-name-input"
          name={`${routePlanningFormConstants.routeName}`}
          control={formProps.control}
          label={t("routeName")}
          placeholder={t("typeRouteName")}
          rules={{
            required: {
              value: true,
              message: t("routeNameRequired"),
            },
          }}
        />
        <Box
          sx={{
            textAlign: "right",
            marginBottom: 1.5,
          }}
        >
          <MenuWrapper
            label="Add bound type"
            data-testid="route-form-add-bound-btn"
            buttonProps={{
              variant: "outlined",
            }}
            endIcon={<AddCircleOutline color="primary" fontSize="small" />}
          >
            {boundOptions.map((bType) => {
              return (
                <MenuItem data-testid={`route-form-bound-${bType}`} key={bType} onClick={() => onChangeBoundType(bType)}>
                  <ListItemText>{t(bType)}</ListItemText>
                </MenuItem>
              )
            })}
          </MenuWrapper>
        </Box>
        {boundList.map((field, index) => {
          return field.boundType === BoundTypeEnum.postal_code ? (
            <PostalCodeListForm key={field.id} parentIndex={index} />
          ) : field.boundType === BoundTypeEnum.user_drawn_area ? (
            <>user_drawn_area</>
          ) : (
            <>Localality</>
          )
        })}
        <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
          <Button data-testid="route-form-cancel-btn" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            data-testid="route-form-submit-btn"
            disabled={!formProps.formState.isValid}
            type="submit"
            variant="contained"
            endIcon={
              (isCreateLoading || isUpdateLoading) && (
                <CircularProgress
                  size={20}
                  sx={{
                    color: "#ffffff",
                  }}
                />
              )
            }
          >
            {routeDetail ? "Update Route" : "Create Route"}
          </Button>
        </Stack>
      </form>
    </FormProvider>
  )
}

const PostalCodeListForm: FC<{ parentIndex: number }> = ({ parentIndex }) => {
  const { t } = useTranslation()
  const { control, watch } = useFormContext<RoutePlanningModel>()
  const postalCodeList = watch(`bounds.${parentIndex}.values`)

  const {
    fields: postalCodes,
    append: appendPostalCode,
    remove,
  } = useFieldArray({
    control,
    name: `bounds.${parentIndex}.values`,
  })
  const removePostalCode = (index: number) => {
    if (postalCodeList.length === 1) return
    remove(index)
  }

  const addPostalCode = () => {
    appendPostalCode(defaultEmptyCode)
  }

  return (
    <>
      <Typography>Postal Code List</Typography>
      {postalCodes.map((field, codeFieldIndex) => {
        return (
          <Box key={field.id} display="flex" alignItems="center" mb={2}>
            <InputFieldWrapper
              data-testid={`route-form-postal-code-${codeFieldIndex}`}
              control={control}
              name={`bounds.${parentIndex}.values.${codeFieldIndex}.code`}
              label={`Postal code ${codeFieldIndex + 1}`}
              placeholder={t("typeHere")}
              margin="dense"
              rules={{
                required: {
                  value: true,
                  message: t("postalCodeRequired"),
                },
                pattern: {
                  value: appRegexConstants.usaPostalCodeRegex,
                  message: t("enterValidPostalCode"),
                },
              }}
            />
            {postalCodeList.length > 1 && (
              <IconButton data-testid={`route-form-postal-code-remove-${codeFieldIndex}`} onClick={() => removePostalCode(codeFieldIndex)}>
                <Delete />
              </IconButton>
            )}
            <IconButton data-testid="route-form-postal-code-add" onClick={() => addPostalCode()}>
              <AddCircleOutline />
            </IconButton>
          </Box>
        )
      })}
    </>
  )
}

const defaultEmptyCode = { code: "" }
